"use client";

import { useEffect, useState } from "react";
import * as ReactDOM from "react-dom";
import { Card, CardContent, CardDescription, CardHeader } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { DotPattern } from "@/components/ui/dot-pattern";
import { ChatwootWidget } from "@/components/ui/chatwoot-widget";
import { ThemeToggle } from "@/components/theme-toggle";
import { cn } from "@/lib/utils";
import { isExpectedDomRemovalError, logDomRemovalError } from "@/lib/errors";
import { Mail } from "lucide-react";
import { Logo } from "@/components/ui/logo";

function useChatwootContainer() {
  const [container, setContainer] = useState<HTMLDivElement | null>(null);

  useEffect(() => {
    const div = document.createElement("div");
    div.id = "chatwoot-portal-container";
    div.style.cssText = "position: fixed; z-index: 9999;";

    document.body.appendChild(div);
    setContainer(div);
    console.log("[Debug] Chatwoot container created and appended to body.", { id: div.id });

    return () => {
      console.log("[Debug] Cleanup initiated for Chatwoot container.", { id: div.id });
      try {
        // Diagnostic logging
        const parent = div.parentNode;
        const isInBody = document.body.contains(div);
        console.log("[Debug] Pre-removal state:", {
          id: div.id,
          "div.parentNode": parent?.nodeName,
          "document.body.contains(div)": isInBody,
          "parent === document.body": parent === document.body,
        });
        
        if (isInBody) {
          // The original logic assumes it is a direct child of `body`.
          // If the parent is no longer `body`, this will fail.
          document.body.removeChild(div);
          console.log("[Debug] Successfully removed child from document.body.");
        } else if (parent) {
          // Fallback for when the node is still in the DOM but was moved.
          console.log("[Debug] Fallback: Removing child from its current parent.", { parentNodeName: parent.nodeName });
          parent.removeChild(div);
          console.log("[Debug] Successfully removed child from its actual parent.");
        } else {
          console.log("[Debug] Node is not in the DOM, no removal needed.");
        }
      } catch (error) {
        console.error("[Debug] Error during Chatwoot container cleanup:", error);
        if (!isExpectedDomRemovalError(error)) {
          logDomRemovalError("Chatwoot portal cleanup", error);
        }
      }
      setContainer(null);
    };
  }, []);

  return container;
}

// Página de boas-vindas informativa. Orienta o cliente a usar o magic link enviado por email/WhatsApp. A autenticação real ocorre em /auth/$hub_login.tsx.
export function WelcomePage() {
  const chatwootContainer = useChatwootContainer();

  return (
    <div className="min-h-screen flex items-center justify-center p-4 sm:p-6 lg:p-8 pb-24 relative overflow-hidden bg-gradient-to-br from-neutral-50 via-blue-50/30 to-purple-50/30 dark:from-background dark:via-background dark:to-background">
      {/* Background Pattern */}
      <DotPattern
        className={cn(
          "[mask-image:radial-gradient(600px_circle_at_center,white,transparent)]",
          "fixed inset-0 h-full w-full",
          "text-neutral-400/40 dark:text-neutral-600/60"
        )}
      />

      {/* Theme Toggle */}
      <div className="absolute top-4 right-4 z-10">
        <ThemeToggle />
      </div>

      {/* Main Content */}
      <div className="w-full max-w-sm xs:max-w-md sm:max-w-lg relative z-10">
        <Card className="relative overflow-hidden rounded-3xl border border-slate-200/70 bg-white/95 shadow-[0_12px_40px_rgba(15,23,42,0.08)] backdrop-blur-xl transition-shadow duration-500 supports-[backdrop-filter]:bg-white/85 dark:border-border/50 dark:bg-background/95 dark:shadow-[0_20px_56px_rgba(8,15,30,0.55)] hover:shadow-[0_16px_48px_rgba(15,23,42,0.12)]">
          <CardHeader className="text-center space-y-4 px-8 pt-8 sm:px-10 sm:pt-12">
            <div className="mx-auto h-16">
              <Logo className="h-16" />
            </div>

            <div className="space-y-2">
              <h1 className="relative inline-flex items-center justify-center font-heading text-transparent text-[clamp(1.75rem,4vw,2.75rem)] font-semibold tracking-tight bg-gradient-to-r from-sky-600 via-indigo-600 to-fuchsia-600 dark:from-sky-500 dark:via-indigo-500 dark:to-fuchsia-500 bg-clip-text text-shadow-lg">
                SABHIA HUB
              </h1>
              <CardDescription className="text-muted-foreground text-base sm:text-base">
                Acesse seus livros personalizados gerados por IA
              </CardDescription>
            </div>
          </CardHeader>

          <CardContent className="space-y-8 px-8 pb-10 sm:px-10 sm:pb-12">
            <section className="flex flex-col items-center text-center space-y-6">
              <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-blue-100 to-blue-50 dark:from-blue-900/30 dark:to-blue-900/10 border border-blue-200/60 dark:border-blue-800/40 flex items-center justify-center shadow-sm">
                <Mail className="w-10 h-10 text-blue-600 dark:text-blue-400" />
              </div>

              <div className="space-y-2">
                <h2 className="text-2xl font-semibold text-foreground">Verifique seu Email e WhatsApp</h2>
                <p className="text-base text-muted-foreground">
                  Enviamos um link de acesso direto para o email e WhatsApp cadastrados na sua compra.
                </p>
              </div>

              <div className="w-full rounded-2xl border border-blue-200/60 dark:border-blue-800/40 bg-gradient-to-br from-blue-500/10 via-purple-500/10 to-transparent p-6 text-left">
                <h3 className="text-lg font-semibold text-foreground mb-3">Como acessar</h3>
                <ul className="space-y-2 text-sm text-muted-foreground">
                  <li>1. <strong>Acesse o link mágico</strong> que enviamos para seu email ou WhatsApp.</li>
                  <li>2. Uma vez autenticado, você será redirecionado para a plataforma.</li>
                  <li>3. Depois disso, basta acessar <strong>livros.sabhia.com.br</strong> sempre que precisar.</li>
                </ul>
              </div>
            </section>


            <section className="space-y-3 text-center">
              <h3 className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">Precisa de ajuda?</h3>
              <p className="text-sm text-muted-foreground">
                Não recebeu o email? Verifique sua caixa de spam ou lixo eletrônico.
              </p>
              <Button
                variant="secondary"
                className="w-full sm:w-auto"
                onClick={() => window.open('https://sabhia.me/atendimento', '_blank')}
              >
                Falar com o Suporte
              </Button>
            </section>
          </CardContent>
        </Card>
      </div>

      {/* Chatwoot via portal apenas para isolar o ciclo de vida React: o SDK ainda injeta no document global e React garante que desmontagens ocorram antes da remoção do container (ver checagem em useChatwootContainer). Se um dia o SDK aceitar container customizado, podemos propagar o elemento daqui para o init. */}
      {chatwootContainer && ReactDOM.createPortal(
        <ChatwootWidget />,
        chatwootContainer
      )}
    </div>
  );
}